import { Dispatch, SetStateAction } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import {
	deleteConsistencyProgressId,
	getConsistencyProgressId,
	launchConsistencyProgress,
	runConsistencyProgress,
} from '@api/consistencyCheck';

import { formatDate } from '@utils/date';
import { handleErrorFromMessage } from '@utils/handleRequestError';
import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { IBranchProgress } from '@interfaces/cmModule/cmModule';
import { IConsistencyCheckBranchesDTO } from '@interfaces/cmModule/dto/consistencyCheckDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { EVendorsValues } from '@enums/common';

import {
	CONSISTENCY_GET_PROGRESSES,
	CONSISTENCY_RUN_PROGRESSES,
} from '@constants/queryKeys';
import { FULL_PERCENT, PROGRESS_INTERVAL } from '../constants';

interface IUseConsistencyCheckGenerateBranchesReturnType {
	progressId?: string;
	generateProgressId: UseMutateFunction<string, any, void, any>;
	progressData?: IBranchProgress;
}

interface IUseConsistencyCheckGenerateBranchesParams extends Omit<IConsistencyCheckBranchesDTO, 'progressId'> {
	isProgressRun: boolean;
	generateBranches: (params: IConsistencyCheckBranchesDTO) => void;
	setIsProgressRun: Dispatch<SetStateAction<boolean>>;
}

const useConsistencyCheckGenerateBranches = (
	params: IUseConsistencyCheckGenerateBranchesParams,
): IUseConsistencyCheckGenerateBranchesReturnType => {
	const {
		vendor,
		technology,
		nodeType,
		configurationDate,
		setIsProgressRun,
		isProgressRun,
		nodes,
		type,
		version,
	} = params;

	const isMavenirVendor = vendor === EVendorsValues.mavenir;

	const isEnabled = isMavenirVendor
		? [!!vendor, !!configurationDate, !!version, !!type, !!nodes]
		: [!!vendor, !!technology, !!nodeType, !!configurationDate];

	const {
		data: progressId,
		mutate: generateProgressId,
	} = useMutation<void, string>({
		mutationFn: launchConsistencyProgress,
		mutationKey: [CONSISTENCY_RUN_PROGRESSES],
		onSuccess: (id) => {
			if (id && isEnabled) {
				setIsProgressRun(true);
				void runConsistencyProgress({
					vendor,
					technology,
					nodeType: nodeType,
					configurationDate: startOfDayInUTC(new Date(configurationDate!)),
					configurationDateText: formatDate(configurationDate!),
					progressId: id,
					version,
					type,
					nodes,
				});
			} else setIsProgressRun(false);
		},
		onError: (err: IResponseError) => {
			setIsProgressRun(false);
			handleErrorFromMessage(err);
		},
	});

	const { data: progressData } = useQuery<IBranchProgress | undefined>({
		queryKey: [
			CONSISTENCY_GET_PROGRESSES,
			progressId,
		],
		queryFn: async () => {
			if (progressId) return getConsistencyProgressId(progressId);

			setIsProgressRun(false);
		},
		onSuccess: (data) => {
			if (data?.percentsProgress == FULL_PERCENT && progressId) {
				if (isProgressRun) void deleteConsistencyProgressId(progressId);

				setIsProgressRun(false);
			}
		},
		onError: (err: IResponseError) => {
			setIsProgressRun(false);
			handleErrorFromMessage(err);
		},
		enabled: [!!progressId, isProgressRun],
		refetchInterval: isProgressRun ? PROGRESS_INTERVAL : false,
	});

	return {
		progressId,
		generateProgressId,
		progressData,
	};
};

export default useConsistencyCheckGenerateBranches;
